import {ErrorResponse, SuccessResponse} from "../../types";
import {ERROR_STATUS, VALIDATE_ERROR} from "../../constans";
import $ from 'jquery';
import IMask from 'imask';
import EventBusService from "../../services/EventBusService";
import PopupManager from "../../managers/PopupManager";

export type SimpleFormSettings = {
    noScrollToError: boolean;
};

export default class SimpleForm {
    private readonly ERROR_CLASS = 'has-error';
    private readonly SUCCESS_CLASS = 'success-state';
    private SUCCESS_POPUP_TYPE = 'success-form';

    private readonly $holder: JQuery;
    private readonly $form: JQuery;
    private readonly $buttonSubmit: JQuery;
    private readonly submitUrl: string;
    private readonly validateUrl: string;
    private readonly hasSuccessBlock: boolean;
    private popupManager: PopupManager;

    private readonly selectors = {
        field: '.js-simple-form-field',
        fieldHolder: '.js-simple-form-holder',
        fieldPhone: '.js-simple-telephone',
        errorBlock: '.js-simple-form-error',
        submit: '.js-form-submit',
        applicationSection: '.js-application-section'
    };

    private settings: SimpleFormSettings = {
        noScrollToError: false,
    };

    private loading = false;

    constructor($holder: JQuery, popupManager: PopupManager, options: SimpleFormSettings | null = null) {
        this.$holder = $holder;
        this.$form = this.$holder.find('form');
        this.$buttonSubmit = this.$form.find(this.selectors.submit);
        this.popupManager = popupManager;

        const submitUrl: string =
            (this.$holder.data('submitUrl') as string) || '';
        const validateUrl: string =
            (this.$holder.data('validateUrl') as string) || '';

        this.hasSuccessBlock =  this.$holder.closest(this.selectors.applicationSection).data('has-success');

        this.submitUrl = submitUrl;
        this.validateUrl = validateUrl;

        this.settings = $.extend({}, this.settings, options);

        this.initEvents = this.initEvents.bind(this);
        this.clearAndResetForm = this.clearAndResetForm.bind(this);
        this.showSuccessPopup = this.showSuccessPopup.bind(this);
        this.init();
    }

    /**
     * Инициализировать класс
     * @private
     */
    private init(): void {
        this.initEvents();
    }

    /**
     * Навесить обработчик
     * @private
     */
    private initEvents(): void {
        this.$form.on('submit', (evt) => {
            evt.preventDefault();
            this.submitForm();
        });

        this.$form.find(this.selectors.fieldPhone).each((_, item) => {
            const maskOptions = {
                mask: '+{7}(000)0000000',
                lazy: true,
            };

            const mask = IMask(item, maskOptions);

            $(item).on('focus', () => {
                mask.updateValue();
                mask.updateOptions({
                    lazy: false,
                });
            });

            $(item).on('blur', () => {
                mask.updateOptions({ lazy: true });
            });
        });
    }

    /**
     * Отправить форму
     * @private
     */
    private submitForm(): void {
        const form = this.$form.get(0);
        this.clearErrors();

        if (!form || this.loading) {
            return;
        }

        const formData = new FormData(form as HTMLFormElement);

        this.$buttonSubmit.addClass('loading');
        this.loading = true;
        void $.ajax({
            url: this.submitUrl,
            type: 'POST',
            dataType: 'json',
            data: formData,
            processData: false,
            contentType: false,
            success: (response: SuccessResponse | ErrorResponse) => {
                this.$buttonSubmit.removeClass('loading');
                this.loading = false;
                const $applicationSection = this.$holder.closest(this.selectors.applicationSection);

                if ($applicationSection.length && this.hasSuccessBlock) {
                    $applicationSection.addClass(this.SUCCESS_CLASS);
                } else {
                    this.showSuccessPopup();
                }

                this.clearErrors();
                if (
                    'error' in response &&
                    response.error.code === VALIDATE_ERROR
                ) {
                    this.processError(response.error.message);

                    return;
                }

                EventBusService.triggerEvent(EventBusService.SIMPLE_FORM_SUCCESS_EVENT_NAME);
                this.resetForm();
            },
            error: (error) => {
                this.$buttonSubmit.removeClass('loading');
                this.loading = false;
                this.clearErrors();
                if (error.status === ERROR_STATUS) {
                    this.processError(error.responseJSON?.errors ?? {});
                } else {
                    this.showSuccessState();
                }
            }
        });
    }

    /**
     * Сбросить форму
     * @private
     */
    private resetForm(): void {
        const form = this.$form.get(0);

        if (!form) {
            return;
        }

        (form as HTMLFormElement).reset();
    }

    /**
     * Очистить ошибки
     * @private
     */
    private clearErrors(): void {
        this.$holder.find(this.selectors.fieldHolder).removeClass(this.ERROR_CLASS);
        this.$holder.find(this.selectors.errorBlock).html('');
    }

    /**
     * Показать состояние «Форма отправлена» (блок успеха или попап, сброс формы)
     * @private
     */
    private showSuccessState(): void {
        const $applicationSection = this.$holder.closest(this.selectors.applicationSection);
        if ($applicationSection.length && this.hasSuccessBlock) {
            $applicationSection.addClass(this.SUCCESS_CLASS);
        } else {
            this.showSuccessPopup();
        }
        this.clearErrors();
        EventBusService.triggerEvent(EventBusService.SIMPLE_FORM_SUCCESS_EVENT_NAME);
        this.resetForm();
    }

    /**
     * Обработать ошибки
     * @param errors
     * @private
     */
    private processError(errors: Record<string, Array<string>>): void {
        const $fields = this.$holder.find(this.selectors.field);

        $fields.each((index, field) => {
            let name = $(field).attr('name') ?? '';
            if (name.includes('data')) {
                name = name.replace(/[[\]]/g, '').replace(/data/, '');
            }

            let errorName = '';
            for (const key in errors) {
                if (key.includes(name)) {
                    errorName = key;
                }
            }

            if (!errorName.length) {
                return;
            }

            const $fieldHolder = $(field).closest(this.selectors.fieldHolder);
            const $errorBlock = $fieldHolder.find(this.selectors.errorBlock);

            $fieldHolder.addClass(this.ERROR_CLASS);
            $errorBlock.text(errors[errorName].join(', '));
        });
    }

    /**
     * Очистка формы и сброс ошибок
     * @private
     */
    private clearAndResetForm(): void {
        this.resetForm();
        this.clearErrors();
    }

    private showSuccessPopup(): void {
        this.popupManager.openPopupByType(this.SUCCESS_POPUP_TYPE, false);
    }
}
